import { useNavigate } from 'react-router-dom';

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, addDoc, doc, updateDoc, serverTimestamp, getDoc, } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../../firebase';
import accessoryCatalog from '../../config/accessories';
import { getWindowBasePrice } from '../../config/windowPricing';
import {
  productCategories,
  colorOptions,
  glassOptions,
  finishOptions,
  getCategoryTitle,
  flattenDesignCatalog,
} from '../../config/productCatalog';
import html2pdf from 'html2pdf.js';
import './AdminInvoices.css';
import { useAuth } from '../../components/AuthContext';

import {
  HiPlus,
  HiXMark,
  HiTrash,
  HiEnvelope,       // ✅ este es el ícono de correo
  HiPencilSquare,
  HiDocumentText,
  HiCheck,
  HiPencil,
  HiPaperAirplane,
  
} from 'react-icons/hi2';

const downloadInvoicePdf = async (invoiceData) => {
  const logoUrl = 'https://puertoricowindows.com/favicon.ico';
  const issueDate = new Date(invoiceData.createdAt?.toDate?.() || Date.now()).toLocaleDateString('es-PR', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  // ✅ Obtener nombre del cliente desde invoiceData o Firestore
  let clientName = invoiceData.clientName;

  if (!clientName && invoiceData.clientId) {
    try {
      const clientSnap = await getDoc(doc(db, 'clients', invoiceData.clientId));
      if (clientSnap.exists()) {
        clientName = clientSnap.data().businessName || '';
      }
    } catch (error) {
      console.error("❌ Error al obtener el nombre del cliente:", error);
    }
  }

  const itemsHtml = (invoiceData.items || []).map(item => `
    <tr>
      <td style="padding: 10px; border: 1px solid #ccc;">${item.name || '-'}</td>
      <td style="padding: 10px; border: 1px solid #ccc;">${item.description || '-'}</td>
      <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">${item.quantity || 1}</td>
      <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">$${parseFloat(item.price || 0).toFixed(2)}</td>
      <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">$${(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)}</td>
    </tr>
  `).join('');

  const abonos = (invoiceData.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const resumenHtml = `
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Total</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.total || 0).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Depósito</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.deposit || 0).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Descuento</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.discount || 0).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Abonos</td><td style="padding: 10px; text-align: right;">$${abonos.toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; font-weight: bold; text-align: right;">Saldo Restante</td><td style="padding: 10px; font-weight: bold; text-align: right;">$${parseFloat(invoiceData.remaining || 0).toFixed(2)}</td></tr>
  `;

  const html = `
    <div style="font-family: Arial, sans-serif; padding: 20px;">
      <div style="text-align: center; border-bottom: 2px solid #1976d2; margin-bottom: 20px;">
        <img src="${logoUrl}" alt="Logo Puerto Rico Windows & Doors" style="width: 80px;" />
        <h2 style="color: #1976d2;">Factura Puerto Rico Windows & Doors</h2>
      </div>

      <p><strong>Fecha:</strong> ${issueDate}</p>
      <p><strong>Cliente:</strong> ${clientName || 'N/A'}</p>
      <p><strong>Email:</strong> ${invoiceData.clientEmail}</p>
      <p><strong>ID de Factura:</strong> ${invoiceData.id || 'N/A'}</p>

      <h3>Ítems Facturados</h3>
      <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
        <thead style="background-color: #1976d2; color: white;">
          <tr>
            <th style="padding: 10px; border: 1px solid #ccc;">Servicio</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Descripción</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Cantidad</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Precio</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
          ${resumenHtml}
        </tbody>
      </table>

      <h3>Notas</h3>
      <p>${invoiceData.notes || '—'}</p>

      <h3>Estado</h3>
      <p><strong>${invoiceData.status?.toUpperCase() || 'PENDIENTE'}</strong></p>
    </div>
  `;

  html2pdf().from(html).save(`Factura-Puerto-Rico-Windows-And-Doors-${invoiceData.id || 'sin-id'}.pdf`);
};

const buildInvoiceHtml = (invoiceData, clientName, clientEmail) => {
  const logoUrl = 'https://puertoricowindows.com/favicon.ico';
  const issueDate = new Date(invoiceData.createdAt?.toDate?.() || Date.now()).toLocaleDateString('es-PR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const itemsHtml = (invoiceData.items || [])
    .map((item) => {
      const quantity = item.quantity || 1;
      const price = parseFloat(item.price || 0);
      const total = (parseFloat(item.quantity || 0) * price) || price;
      return `
        <tr>
          <td style="padding: 10px; border: 1px solid #ccc;">${item.name || '-'}</td>
          <td style="padding: 10px; border: 1px solid #ccc;">${item.description || '-'}</td>
          <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">${quantity}</td>
          <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">$${price.toFixed(2)}</td>
          <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">$${total.toFixed(2)}</td>
        </tr>
      `;
    })
    .join('');

  const paymentsTotal = (invoiceData.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const resumenHtml = `
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Total</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.total || 0).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Depósito</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.deposit || 0).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Descuento</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.discount || 0).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; text-align: right;">Abonos</td><td style="padding: 10px; text-align: right;">$${paymentsTotal.toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding: 10px; font-weight: bold; text-align: right;">Saldo Restante</td><td style="padding: 10px; font-weight: bold; text-align: right;">$${parseFloat(invoiceData.remaining || 0).toFixed(2)}</td></tr>
  `;

  return `
    <div style="font-family: Arial, sans-serif; padding: 20px;">
      <div style="text-align: center; border-bottom: 2px solid #1976d2; margin-bottom: 20px;">
        <img src="${logoUrl}" alt="Logo Puerto Rico Windows & Doors" style="width: 80px;" />
        <h2 style="color: #1976d2;">Factura Puerto Rico Windows & Doors</h2>
      </div>

      <p><strong>Fecha:</strong> ${issueDate}</p>
      <p><strong>Cliente:</strong> ${clientName || 'N/A'}</p>
      <p><strong>Email:</strong> ${clientEmail || invoiceData.clientEmail || 'N/A'}</p>
      <p><strong>ID de Factura:</strong> ${invoiceData.id || 'N/A'}</p>

      <h3>Ítems Facturados</h3>
      <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
        <thead style="background-color: #1976d2; color: white;">
          <tr>
            <th style="padding: 10px; border: 1px solid #ccc;">Servicio</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Descripción</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Cantidad</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Precio</th>
            <th style="padding: 10px; border: 1px solid #ccc;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
          ${resumenHtml}
        </tbody>
      </table>

      <h3>Notas</h3>
      <p>${invoiceData.notes || '—'}</p>

      <h3>Estado</h3>
      <p><strong>${invoiceData.status?.toUpperCase() || 'PENDIENTE'}</strong></p>
    </div>
  `;
};

const generateInvoicePdfBlob = async (invoiceData, clientName, clientEmail) => {
  const html = buildInvoiceHtml(invoiceData, clientName, clientEmail);
  return html2pdf()
    .from(html)
    .set({
      margin: 0.5,
      filename: `Factura-${invoiceData.id || 'PRWD'}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    })
    .outputPdf('blob');
};

const ensureInvoicePdfUrl = async (invoiceData, clientName, clientEmail) => {
  if (invoiceData.invoicePdfUrl) return invoiceData.invoicePdfUrl;

  const blob = await generateInvoicePdfBlob(invoiceData, clientName, clientEmail);
  const safeClient =
    (invoiceData.clientId || invoiceData.clientEmail || 'sin-cliente')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .slice(0, 60);
  const safeInvoice = (invoiceData.id || `inv_${Date.now()}`).replace(/[^a-zA-Z0-9_-]/g, '_');
  const storagePath = `invoices/${safeClient}/${safeInvoice}.pdf`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, blob, { contentType: 'application/pdf' });
  const url = await getDownloadURL(storageRef);

  if (invoiceData.clientId && invoiceData.id) {
    try {
      await updateDoc(doc(db, 'clients', invoiceData.clientId, 'invoices', invoiceData.id), {
        invoicePdfUrl: url,
        invoicePdfStoragePath: storagePath,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('No se pudo actualizar la factura con el PDF:', err);
    }
  }

  return url;
};


const calculateRemaining = (invoice) => {
  const total = parseFloat(invoice.total || 0);
  const deposit = parseFloat(invoice.deposit || 0);
  const discount = parseFloat(invoice.discount || 0);
  const payments = (invoice.payments || []).reduce(
    (acc, p) => acc + parseFloat(p.amount || 0),
    0
  );
  return total - deposit - discount - payments;
};

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-PR', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);

const buildCatalogSummary = (items = []) => {
  const summary = {
    models: [],
    accessories: [],
    subtotal: 0,
  };

  items.forEach((item) => {
    const quantity = Number(item.quantity || 0);
    const price = Number(item.price || 0);
    const lineTotal = quantity * price;
    summary.subtotal += lineTotal;

    if (item.catalogMeta?.productId) {
      summary.models.push({
        productId: item.catalogMeta.productId,
        categoryId: item.catalogMeta.categoryId,
        width: item.catalogMeta.width || null,
        height: item.catalogMeta.height || null,
        series: item.catalogMeta.series || null,
        color: item.catalogMeta.color || null,
        glass: item.catalogMeta.glass || null,
        finish: item.catalogMeta.finish || null,
        autoPriceInfo: item.catalogMeta.autoPriceInfo || null,
        quantity,
        unitPrice: price,
        total: lineTotal,
        name: item.name,
        description: item.description,
      });
    } else if (item.catalogMeta?.accessoryId) {
      summary.accessories.push({
        accessoryId: item.catalogMeta.accessoryId,
        quantity,
        unitPrice: price,
        total: lineTotal,
        name: item.name,
        description: item.description,
      });
    }
  });

  if (!summary.models.length && !summary.accessories.length) {
    return null;
  }
  return summary;
};

const defaultCatalogBuilder = {
  categoryId: 'ventanas',
  productId: '',
  width: '',
  height: '',
  quantity: 1,
  series: 'lama3',
  manualPrice: '',
  color: 'blanco',
  glass: 'clear',
  finish: 'mate',
  accessories: {},
};

const InvoiceModal = ({
  invoice,
  creating,
  onClose,
  onChange,
  onSave,
  onAddPayment,
  newPayment,
  setNewPayment,
  sendInvoiceEmail,
  catalogProducts = [],
  catalogAccessories = [],
  catalogLoading = false,
}) => {
  const [builder, setBuilder] = useState(defaultCatalogBuilder);
  const [builderMessage, setBuilderMessage] = useState('');
  useEffect(() => {
    setBuilder((prev) => ({
      ...defaultCatalogBuilder,
      categoryId: prev.categoryId || 'ventanas',
    }));
    setBuilderMessage('');
  }, [invoice]);

  const handleBuilderChange = (field, value) => {
    setBuilder((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccessoryChange = (id, quantity) => {
    const qty = Math.max(0, Number(quantity) || 0);
    setBuilder((prev) => ({
      ...prev,
      accessories: { ...(prev.accessories || {}), [id]: qty },
    }));
  };
  const productOptions = (catalogProducts || []).filter(
    (product) => product.categoryId === (builder.categoryId || 'ventanas')
  );
  const selectedProduct =
    productOptions.find((item) => item.id === builder.productId) || productOptions[0];
  useEffect(() => {
    if (!builder.productId && selectedProduct) {
      setBuilder((prev) => ({ ...prev, productId: selectedProduct.id }));
    }
  }, [selectedProduct, builder.productId]);

  if (!invoice) return null;

  const isWindowCategory = builder.categoryId === 'ventanas';
  const builderWidth = Number(builder.width);
  const builderHeight = Number(builder.height);
  const autoPriceInfo =
    isWindowCategory && builderWidth > 0 && builderHeight > 0
      ? getWindowBasePrice(builderWidth, builderHeight, builder.series || 'lama3')
      : null;
  const parsedManual = Number(builder.manualPrice);
  const builderUnitPrice =
    parsedManual > 0
      ? parsedManual
      : isWindowCategory
      ? autoPriceInfo?.price || 0
      : 0;

  const accessoryOptions = (catalogAccessories || []).filter(
    (acc) => !acc.appliesTo || acc.appliesTo.includes(builder.categoryId)
  );
  const resetBuilder = () => {
    setBuilder((prev) => ({
      ...defaultCatalogBuilder,
      categoryId: prev.categoryId,
      series: prev.series,
    }));
    setBuilderMessage('');
  };
  const updateItem = (index, field, value) => {
    const updatedItems = [...(invoice.items || [])];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    onChange({ ...invoice, items: updatedItems });
  };

  const removeItem = (index) => {
    const updatedItems = [...(invoice.items || [])];
    updatedItems.splice(index, 1);
    onChange({ ...invoice, items: updatedItems });
  };

  const addItem = () => {
    const newItems = [...(invoice.items || []), { name: '', description: '', quantity: 1, price: 0 }];
    onChange({ ...invoice, items: newItems });
  };

  const handleAddFromCatalog = () => {
    if (!builder.productId) {
      setBuilderMessage('Selecciona un modelo del catálogo.');
      return;
    }
    const qty = Math.max(1, Number(builder.quantity) || 1);
    if (!builderUnitPrice) {
      setBuilderMessage(
        isWindowCategory
          ? 'Ingresa ancho/alto válidos o un precio manual para este modelo.'
          : 'Ingresa el precio base del modelo seleccionado.'
      );
      return;
    }

    const descriptionParts = [];
    if (builderHeight && builderWidth) {
      descriptionParts.push(`${builderHeight}" x ${builderWidth}"`);
    }
    descriptionParts.push(`Color ${builder.color}`);
    descriptionParts.push(`Cristal ${builder.glass}`);
    descriptionParts.push(`Acabado ${builder.finish}`);
    if (isWindowCategory) {
      descriptionParts.push(`Serie ${builder.series?.replace('lama', 'Lama ')}`);
      if (autoPriceInfo?.widthExceeded) {
        descriptionParts.push('⚠︎ Excede tabla 36" · requiere validación de ingeniería');
      }
    }

    const accessoryEntries = Object.entries(builder.accessories || {}).filter(
      ([, value]) => Number(value) > 0
    );
    const accessoriesApplied = accessoryEntries
      .map(([accessoryId, quantity]) => {
        const info = accessoryOptions.find((acc) => acc.id === accessoryId);
        if (!info) return null;
        return {
          id: accessoryId,
          name: info.name,
          description: info.description,
          quantity: Number(quantity),
          price: info.price,
        };
      })
      .filter(Boolean);

    if (accessoriesApplied.length) {
      descriptionParts.push(
        `Accesorios: ${accessoriesApplied.map((addon) => `${addon.name} (${addon.quantity})`).join(', ')}`
      );
    }

    const productLabel = selectedProduct
      ? `${selectedProduct.title} · ${getCategoryTitle(selectedProduct.categoryId)}`
      : `Modelo ${builder.categoryId}`;

    const newItems = [
      ...(invoice.items || []),
      {
        name: productLabel,
        description: descriptionParts.join(' · '),
        quantity: qty,
        price: builderUnitPrice,
        catalogMeta: {
          productId: builder.productId,
          categoryId: builder.categoryId,
          width: builder.width,
          height: builder.height,
          series: builder.series,
          color: builder.color,
          glass: builder.glass,
          finish: builder.finish,
          autoPriceInfo,
        },
      },
    ];

    accessoriesApplied.forEach((addon) => {
      newItems.push({
        name: `Accesorio · ${addon.name}`,
        description: addon.description || '',
        quantity: addon.quantity,
        price: addon.price,
        catalogMeta: {
          accessoryId: addon.id,
        },
      });
    });

    onChange({ ...invoice, items: newItems });
    resetBuilder();
  };
  const calculateItemTotal = (item) => {
    const q = parseFloat(item.quantity || 0);
    const p = parseFloat(item.price || 0);
    return (q * p).toFixed(2);
  };

  const calculateInvoiceTotal = () => {
    const items = invoice.items || [];
    const total = items.reduce((sum, item) => {
      const q = parseFloat(item.quantity || 0);
      const p = parseFloat(item.price || 0);
      return sum + (q * p);
    }, 0);
    return total.toFixed(2);
  };

  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center px-4">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg overflow-y-auto max-h-[90vh]">
        <header className="flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            {creating ? (
              <>
                <HiPlus className="w-5 h-5 text-orange-500" />
                Crear Factura
              </>
            ) : (
              <>
                <HiPencilSquare className="w-5 h-5 text-blue-500" />
                Editar Factura
              </>
            )}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-red-600 transition">
            <HiXMark className="w-6 h-6" />
          </button>
        </header>
  
        <div className="px-6 py-4 space-y-6">
          <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gray-500">
                  Catálogo de modelos y accesorios
                </p>
                <h4 className="text-lg font-semibold text-gray-900">Agregar desde catálogo oficial</h4>
                <p className="text-sm text-gray-500">
                  Selecciona el modelo, ancho y alto en pulgadas. El sistema usa la tabla de lamas como referencia.
                </p>
              </div>
              {catalogLoading && (
                <span className="inline-flex items-center rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-500">
                  Cargando catálogo…
                </span>
              )}
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Categoría
                </label>
                <select
                  value={builder.categoryId}
                  onChange={(e) => handleBuilderChange('categoryId', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                >
                  {productCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Modelo
                </label>
                <select
                  value={builder.productId || ''}
                  onChange={(e) => handleBuilderChange('productId', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                >
                  {productOptions.length === 0 && <option>Sin modelos disponibles</option>}
                  {productOptions.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Cantidad
                </label>
                <input
                  type="number"
                  min="1"
                  value={builder.quantity}
                  onChange={(e) => handleBuilderChange('quantity', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                />
              </div>
              {isWindowCategory && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Serie (lama)
                  </label>
                  <select
                    value={builder.series}
                    onChange={(e) => handleBuilderChange('series', e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {['lama3', 'lama4', 'lama6'].map((series) => (
                      <option key={series} value={series}>
                        {series.replace('lama', 'Lama ')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Alto (pulgadas)
                </label>
                <input
                  type="number"
                  min="0"
                  value={builder.height}
                  onChange={(e) => handleBuilderChange('height', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  placeholder="Ej. 72"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Ancho (pulgadas)
                </label>
                <input
                  type="number"
                  min="0"
                  value={builder.width}
                  onChange={(e) => handleBuilderChange('width', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  placeholder="Ej. 48"
                />
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Color
                </label>
                <select
                  value={builder.color}
                  onChange={(e) => handleBuilderChange('color', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                >
                  {colorOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Cristal
                </label>
                <select
                  value={builder.glass}
                  onChange={(e) => handleBuilderChange('glass', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                >
                  {glassOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Acabado
                </label>
                <select
                  value={builder.finish}
                  onChange={(e) => handleBuilderChange('finish', e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                >
                  {finishOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {isWindowCategory && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-sm text-gray-700">
                {autoPriceInfo ? (
                  <>
                    <p className="font-semibold text-emerald-700">
                      Precio sugerido: {formatCurrency(autoPriceInfo.price)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Basado en {autoPriceInfo.lamas} lamas · tabla {autoPriceInfo.series?.replace('lama', 'Lama ')} · ancho{' '}
                      {autoPriceInfo.widthBucket}" · alto ref. {autoPriceInfo.heightReference}"
                    </p>
                    {autoPriceInfo.widthExceeded && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠︎ Se usó el valor de 36". Validar medidas en visita técnica.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Ingresa alto y ancho para calcular según la tabla.</span>
                  </div>
                )}
                <div className="mt-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 flex items-center gap-2">
                    Precio manual (opcional)
                    <span className="text-gray-400 text-[10px] uppercase">USD</span>
                  </label>
                  <input
                    type="number"
                    value={builder.manualPrice}
                    onChange={(e) => handleBuilderChange('manualPrice', e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="Sobrescribir precio calculado"
                  />
                </div>
              </div>
            )}
            {accessoryOptions.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gray-500">
                  Accesorios vinculados
                </p>
                <div className="mt-2 grid gap-3 md:grid-cols-2">
                  {accessoryOptions.map((accessory) => (
                    <label key={accessory.id} className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                      <div>
                        <p className="font-semibold">{accessory.name}</p>
                        <p className="text-xs text-gray-500">{formatCurrency(accessory.price)} · {accessory.unit}</p>
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={builder.accessories?.[accessory.id] || ''}
                        onChange={(e) => handleAccessoryChange(accessory.id, e.target.value)}
                        className="w-20 rounded-xl border border-gray-200 bg-white px-2 py-1 text-right text-sm focus:border-emerald-500 focus:outline-none"
                        placeholder="0"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
            {builderMessage && (
              <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{builderMessage}</p>
            )}
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleAddFromCatalog}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                <HiPlus className="h-4 w-4" />
                Añadir item del catálogo
              </button>
              <button
                type="button"
                onClick={resetBuilder}
                className="text-xs font-semibold text-gray-500 hover:text-gray-700"
              >
                Limpiar selección
              </button>
            </div>
          </div>
          {/* Ítems Facturados */}
          <div>
            <h4 className="text-md font-medium flex items-center gap-2 text-gray-700 mb-2">
              <HiDocumentText className="w-5 h-5 text-gray-500" />
              Ítems Facturados
            </h4>
  
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border rounded">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2">Servicio</th>
                    <th className="px-3 py-2">Descripción</th>
                    <th className="px-3 py-2">Cantidad</th>
                    <th className="px-3 py-2">Precio</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-center">Eliminar</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-3 py-2">
                        <input
                          value={item.name}
                          onChange={e => updateItem(index, 'name', e.target.value)}
                          className="input"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={item.description}
                          onChange={e => updateItem(index, 'description', e.target.value)}
                          className="input"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={e => updateItem(index, 'quantity', e.target.value)}
                          className="input"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.price}
                          onChange={e => updateItem(index, 'price', e.target.value)}
                          className="input"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">${calculateItemTotal(item)}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <HiTrash className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
  
            <button
              onClick={addItem}
              className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
            >
              <HiPlus className="w-4 h-4" />
              Añadir ítem
            </button>
          </div>
  
          {/* Totales y Campos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="text-sm text-gray-700">
              Total (automático)
              <input
                type="number"
                value={calculateInvoiceTotal()}
                readOnly
                className="input mt-1"
              />
            </label>
            <label className="text-sm text-gray-700">
              Depósito
              <input
                type="number"
                value={invoice.deposit}
                onChange={e => onChange({ ...invoice, deposit: e.target.value })}
                className="input mt-1"
              />
            </label>
            <label className="text-sm text-gray-700">
              Descuento
              <input
                type="number"
                value={invoice.discount || ''}
                onChange={e => onChange({ ...invoice, discount: e.target.value })}
                className="input mt-1"
              />
            </label>
            <label className="text-sm text-gray-700">
              Saldo Restante
              <input
                type="number"
                value={calculateRemaining(invoice)}
                readOnly
                className="input mt-1"
              />
            </label>
            <label className="text-sm text-gray-700">
              Estado
              <select
                value={invoice.status}
                onChange={e => onChange({ ...invoice, status: e.target.value })}
                className="input mt-1"
              >
                <option value="pendiente">Pendiente</option>
                <option value="pagada">Pagada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </label>
          </div>
  
          {/* Notas */}
          <label className="block text-sm text-gray-700">
            Notas
            <textarea
              value={invoice.notes}
              onChange={e => onChange({ ...invoice, notes: e.target.value })}
              className="input mt-1"
              rows={3}
            />
          </label>
  
          {/* Abonos */}
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-2">Abonos</h4>
            <ul className="space-y-1 text-sm text-gray-700">
              {(invoice.payments || []).map((p, i) => (
                <li key={i}>
                  ${parseFloat(p.amount).toFixed(2)} – {new Date(p.date).toLocaleDateString()}
                </li>
              ))}
            </ul>
            <div className="flex gap-2 mt-3">
              <input
                type="number"
                placeholder="Monto nuevo"
                value={newPayment}
                onChange={e => setNewPayment(e.target.value)}
                className="input w-full"
              />
              <button
                onClick={onAddPayment}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center gap-1"
              >
                <HiPlus className="w-4 h-4" />
                Añadir
              </button>
            </div>
          </div>
        </div>
  
        <footer className="flex justify-end items-center gap-2 border-t px-6 py-4">
  {/* Botón Guardar */}
  <button
    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center gap-1"
    onClick={() => onSave()}
  >
    <HiCheck className="w-5 h-5" />
    Guardar
  </button>

  {/* Botón Cancelar */}
  <button
    className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
    onClick={onClose}
  >
    Cancelar
  </button>

  {/* Botón Enviar Email */}
  {!creating && invoice?.id && (
    <button
      onClick={() => sendInvoiceEmail(invoice)}
      className="flex items-center gap-2 text-blue-600 hover:underline"
    >
      <HiEnvelope className="w-5 h-5" />
      Enviar Email
    </button>
  )}

  {/* Botón Descargar PDF */}
  {!creating && invoice?.id && (
    <button
      onClick={() => downloadInvoicePdf(invoice)}
      className="flex items-center gap-2 text-green-600 hover:underline"
    >
      <HiDocumentText className="w-5 h-5" />
      Descargar PDF
    </button>
  )}
</footer>

      </div>
    </div>
  );
};

const taskQuickActionInitial = {
  type: 'warehouse',
  dueDate: '',
  notes: '',
  assigneeName: '',
  assigneeEmail: '',
  invoicePdfUrl: '',
  title: '',
};

const AdminInvoices = () => {
  const { clientId } = useParams();
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [mainClientId, setMainClientId] = useState(null);
  const [newPayment, setNewPayment] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientPhoneField, setClientPhoneField] = useState('');
  const [clientLocationField, setClientLocationField] = useState('');
  const [taskModalInvoice, setTaskModalInvoice] = useState(null);
  const [taskForm, setTaskForm] = useState(taskQuickActionInitial);
  const [taskModalSubmitting, setTaskModalSubmitting] = useState(false);
  const [taskFeedback, setTaskFeedback] = useState(null);
  const [prefillingTaskId, setPrefillingTaskId] = useState('');
  const [regeneratingPdf, setRegeneratingPdf] = useState(false);
  useEffect(() => {
    const loadCatalog = () => {
      try {
        setCatalogLoading(true);
        setCatalogProducts(flattenDesignCatalog());
        setCatalogAccessories(accessoryCatalog);
      } finally {
        setCatalogLoading(false);
      }
    };
    loadCatalog();
  }, []);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogAccessories, setCatalogAccessories] = useState([]);
  const navigate = useNavigate();
  const auth = getAuth();
  const { userRole } = useAuth();

  const [currentClientId, setCurrentClientId] = useState('');
  const [currentClientEmail, setCurrentClientEmail] = useState('');
  const [currentUser, setCurrentUser] = useState(() => auth.currentUser);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => setCurrentUser(user));
    return () => unsub();
  }, [auth]);


  useEffect(() => {
    const fetchInvoices = async () => {
      if (!currentClientId || !currentClientEmail) return;
  
      try {
        const invoiceRef = collection(db, 'clients', currentClientId, 'invoices');
        const invoiceSnap = await getDocs(invoiceRef);
  
        const invoices = invoiceSnap.docs.map(doc => ({
          id: doc.id,
          clientId: currentClientId,
          clientEmail: currentClientEmail,
          ...doc.data(),
        }));
  
        setInvoices(invoices);
      } catch (err) {
        console.error('❌ Error al buscar facturas:', err);
      }
    };
  
    fetchInvoices();
  }, [currentClientId, currentClientEmail, refresh]);
  
  
  
  useEffect(() => {
    const fetchClient = async () => {
      const rawClientId = decodeURIComponent(clientId);
      console.log('🔍 rawClientId:', rawClientId);
  
      // 1️⃣ Intentar buscar directamente por ID (puede ser UID o email como doc ID)
      const directRef = doc(db, 'clients', rawClientId);
      const directSnap = await getDoc(directRef);
  
      if (directSnap.exists()) {
        console.log('✅ Cliente encontrado por ID directo:', rawClientId);
        const data = directSnap.data();
  
        setCurrentClientId(directSnap.id);       // Puede ser email o UID
        setCurrentClientEmail(data.email || rawClientId);
        setClientName(data.businessName || data.name || rawClientId);
        setClientPhoneField(data.phone || data.contactPhone || '');
        setClientLocationField(data.projectLocation || data.address || '');
  
        // ✅ Si este documento tiene UID y es diferente al ID que se usó en la URL → REDIRIGIR
        if (data.uid && data.uid !== rawClientId) {
          console.log('🔁 Redirigiendo al UID desde cliente con ID email:', data.uid);
          navigate(`/admin/clients/${data.uid}/invoices`, { replace: true });
          return;
        }
  
        return;
      }
  
      // 2️⃣ Buscar por email dentro de los documentos
      const allClientsSnap = await getDocs(collection(db, 'clients'));
      const match = allClientsSnap.docs.find(docSnap => {
        const docEmail = docSnap.data()?.email;
        return docEmail?.toLowerCase() === rawClientId.toLowerCase();
      });
  
      if (match) {
        const data = match.data();
        console.log('✅ Cliente encontrado por email:', data.email);
        console.log('🆔 match.id (UID real):', match.id);
  
        setCurrentClientId(match.id);
        setCurrentClientEmail(data.email || rawClientId);
        setClientName(data.businessName || data.name || rawClientId);
        setClientPhoneField(data.phone || data.contactPhone || '');
        setClientLocationField(data.projectLocation || data.address || '');
  
        if (match.id !== rawClientId) {
          console.log('🔁 Redirigiendo a UID:', match.id);
          navigate(`/admin/clients/${match.id}/invoices`, { replace: true });
        }
  
        return;
      }
  
      console.error(`❌ Cliente no encontrado por UID ni por email: ${rawClientId}`);
    };
  
    fetchClient();
  }, [clientId]);
  
  
  
  const sendInvoiceEmail = async (invoiceData) => {
    try {
      if (!invoiceData.clientEmail || invoiceData.clientEmail.trim() === '') {
        alert('⚠️ Este cliente no tiene un email asignado. No se puede enviar la factura.');
        return;
      }
  
      const logoUrl = 'https://puertoricowindows.com/favicon.ico';
      const issueDate = new Date(invoiceData.createdAt?.toDate?.() || Date.now()).toLocaleDateString('es-PR', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
  
      const itemsHtml = (invoiceData.items || []).map(item => `
        <tr>
          <td style="padding: 10px; border: 1px solid #ccc;">${item.name || '-'}</td>
          <td style="padding: 10px; border: 1px solid #ccc;">${item.description || '-'}</td>
          <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">${item.quantity || 1}</td>
          <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">$${parseFloat(item.price || 0).toFixed(2)}</td>
          <td style="padding: 10px; border: 1px solid #ccc; text-align: right;">$${(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)}</td>
        </tr>
      `).join('');
  
      const abonos = (invoiceData.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const resumenHtml = `
        <tr><td colspan="4" style="padding: 10px; text-align: right;">Total</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.total || 0).toFixed(2)}</td></tr>
        <tr><td colspan="4" style="padding: 10px; text-align: right;">Depósito</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.deposit || 0).toFixed(2)}</td></tr>
        <tr><td colspan="4" style="padding: 10px; text-align: right;">Descuento</td><td style="padding: 10px; text-align: right;">$${parseFloat(invoiceData.discount || 0).toFixed(2)}</td></tr>
        <tr><td colspan="4" style="padding: 10px; text-align: right;">Abonos</td><td style="padding: 10px; text-align: right;">$${abonos.toFixed(2)}</td></tr>
        <tr><td colspan="4" style="padding: 10px; font-weight: bold; text-align: right;">Saldo Restante</td><td style="padding: 10px; font-weight: bold; text-align: right;">$${parseFloat(invoiceData.remaining || 0).toFixed(2)}</td></tr>
      `;
  
      const html = `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <div style="text-align: center; border-bottom: 2px solid #1976d2; margin-bottom: 20px;">
            <img src="${logoUrl}" alt="Logo Puerto Rico Windows & Doors" style="width: 80px;" />
            <h2 style="color: #1976d2;">Factura Puerto Rico Windows & Doors</h2>
          </div>
  
          <p><strong>Fecha:</strong> ${issueDate}</p>
          <p><strong>Cliente:</strong> ${invoiceData.clientName || 'N/A'}</p>
          <p><strong>Email:</strong> ${invoiceData.clientEmail}</p>
          <p><strong>ID de Factura:</strong> ${invoiceData.id || 'N/A'}</p>
  
          <h3>Ítems Facturados</h3>
          <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
            <thead style="background-color: #1976d2; color: white;">
              <tr>
                <th style="padding: 10px; border: 1px solid #ccc;">Servicio</th>
                <th style="padding: 10px; border: 1px solid #ccc;">Descripción</th>
                <th style="padding: 10px; border: 1px solid #ccc;">Cantidad</th>
                <th style="padding: 10px; border: 1px solid #ccc;">Precio</th>
                <th style="padding: 10px; border: 1px solid #ccc;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
              ${resumenHtml}
            </tbody>
          </table>
  
          <h3>Notas</h3>
          <p>${invoiceData.notes || '—'}</p>
  
          <h3>Estado</h3>
          <p><strong>${invoiceData.status?.toUpperCase() || 'PENDIENTE'}</strong></p>
        </div>
      `;
  
      const pdfBlob = await html2pdf().from(html).set({ format: 'a4' }).outputPdf('blob');
  
      const base64Pdf = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            const result = reader.result.split(',')[1];
            resolve(result);
          } catch (err) {
            reject(new Error('Error al convertir el PDF a Base64'));
          }
        };
        reader.onerror = () => reject(new Error('Error al leer el PDF como Base64'));
        reader.readAsDataURL(pdfBlob);
      });
  
      const response = await fetch('https://sendclientemail-xjpm53xzha-uc.a.run.app/sendClientEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: invoiceData.clientEmail,
          subject: 'Factura Puerto Rico Windows & Doors',
          text: 'Adjunto su factura de Puerto Rico Windows & Doors.',
          html: '<p>Adjunto su factura</p>',
          attachments: [{
            content: base64Pdf,
            filename: 'Factura-Puerto-Rico-Windows-And-Doors.pdf',
            type: 'application/pdf',
            disposition: 'attachment'
          }]
        })
      });
  
      if (!response.ok) {
        const text = await response.text();
        console.error('❌ Error al enviar el email:', response.status, text);
        alert(`Error al enviar el email: ${response.status} - ${text}`);
      } else {
        console.log('📧 Email enviado correctamente ✅');
        alert('Email enviado correctamente al cliente.');
      }
  
    } catch (err) {
      console.error('❌ Error en el proceso de envío de email:', err);
      alert(`Error crítico al enviar el email: ${err.message}`);
    }
  };
  
  
  
  const saveInvoice = async (invoiceData = selectedInvoice) => {
    if (!invoiceData) return;

    const items = invoiceData.items || [];
    const total = items.reduce((sum, item) => {
      const q = parseFloat(item.quantity || 0);
      const p = parseFloat(item.price || 0);
      return sum + q * p;
    }, 0);

    const deposit = parseFloat(invoiceData.deposit || 0);
    const discount = parseFloat(invoiceData.discount || 0);
    const payments = invoiceData.payments || [];
    const remaining =
      total -
      deposit -
      discount -
      payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    let clientId =
      invoiceData.clientId || mainClientId || invoiceData.clientEmail?.toLowerCase();

    if (!clientId) {
      throw new Error(
        '❌ No se pudo determinar el ID del cliente para guardar la factura.'
      );
    }

    let clientName = invoiceData.clientName;

    if (!clientName && invoiceData.clientId) {
      const clientSnap = await getDoc(doc(db, 'clients', invoiceData.clientId));
      if (clientSnap.exists()) {
        clientName = clientSnap.data().businessName || '';
      }
    }

    const creatorInfo =
      invoiceData.createdBy || {
        uid: currentUser?.uid || null,
        email: currentUser?.email || 'admin@puertoricowindows.com',
        role: userRole || 'admin',
        name: currentUser?.displayName || null,
      };
    const sellerId =
      invoiceData.sellerId ||
      (userRole === 'seller' ? currentUser?.uid || null : null);

    const catalogSummary = buildCatalogSummary(items);

    const payload = {
      ...invoiceData,
      total,
      deposit,
      discount,
      payments,
      remaining,
      clientId,
      clientEmail: invoiceData.clientEmail?.toLowerCase?.() || '',
      clientName,
      sellerId,
      catalogSummary,
      hasCatalogItems: Boolean(catalogSummary),
      updatedAt: serverTimestamp(),
      createdAt: invoiceData.createdAt || serverTimestamp(),
    };

    try {
      if (creating) {
        await addDoc(collection(db, 'clients', clientId, 'invoices'), payload);
      } else {
        if (!invoiceData.id) throw new Error('❌ Falta ID');
        const ref = doc(db, 'clients', clientId, 'invoices', invoiceData.id);
        await updateDoc(ref, payload);

        const updatedSnapshot = await getDoc(ref);
        const updatedInvoice = {
          id: invoiceData.id,
          clientId,
          clientEmail: invoiceData.clientEmail,
          ...updatedSnapshot.data(),
        };
        setSelectedInvoice(updatedInvoice);
      }

      setCreating(false);
      setShowModal(false);
      setRefresh((r) => !r);
      setNewPayment('');
    } catch (err) {
      console.error('❌ Error al guardar la factura:', err);
    }
  };
  
  // En AdminInvoices.js
const openInvoiceModal = (invoice) => {
  if (!invoice) return;
  setSelectedInvoice({
    ...invoice,
    clientEmail: currentClientEmail,
    items: invoice.items || [],
    payments: invoice.payments || [],
  });
  setCreating(false);
  setShowModal(true);
};

const handleNew = () => {
  setCreating(true);

  // Asegúrate de incluir clientId y clientEmail en la nueva factura
  setSelectedInvoice({
    clientId: currentClientId,
    clientEmail: currentClientEmail,
    total: '',
    deposit: '',
    discount: '',
    status: 'pendiente',
    notes: '',
    payments: [],
    items: [],
  });
  

  setShowModal(true);
};

const addPayment = () => {
  const amount = parseFloat(newPayment);
  if (isNaN(amount) || amount <= 0 || !selectedInvoice) return;

  const updatedPayments = [...(selectedInvoice.payments || []), {
    amount,
    date: new Date().toISOString(),
  }];

  const updatedInvoice = {
    ...selectedInvoice,
    payments: updatedPayments,
  };

  setSelectedInvoice(updatedInvoice);
  setNewPayment('');

  // ✅ Guardar inmediatamente el abono, esté creando o editando
  saveInvoice(updatedInvoice);
};

  const handleTaskFormChange = (event) => {
    const { name, value } = event.target;
    setTaskForm((prev) => ({ ...prev, [name]: value }));
  };

  const openTaskModalFromInvoice = async (invoice) => {
    if (!invoice) return;
    setTaskFeedback(null);
    setPrefillingTaskId(invoice.id);
    try {
      const suffix = invoice.id ? invoice.id.slice(-5) : '';
      const pdfUrl =
        invoice.invoicePdfUrl ||
        invoice.invoiceAttachmentUrl ||
        (await ensureInvoicePdfUrl(invoice, clientName || invoice.clientName || '', invoice.clientEmail || currentClientEmail || ''));

      setTaskForm({
        ...taskQuickActionInitial,
        type: invoice.status === 'paid' ? 'delivery' : 'warehouse',
        invoicePdfUrl: pdfUrl,
        title: `Instrucciones ${suffix} · ${clientName || invoice.clientName || ''}`,
      });
      setTaskModalInvoice({ ...invoice, invoicePdfUrl: pdfUrl });
    } catch (error) {
      console.error('❌ No se pudo preparar el PDF:', error);
      setTaskFeedback({
        type: 'error',
        message: error.message || 'No se pudo generar el PDF de la factura. Intenta nuevamente.',
      });
    } finally {
      setPrefillingTaskId('');
    }
  };

  const closeTaskModal = () => {
    setTaskModalInvoice(null);
    setTaskForm(taskQuickActionInitial);
    setTaskModalSubmitting(false);
    setRegeneratingPdf(false);
  };

  const submitTaskFromInvoice = async (event) => {
    event.preventDefault();
    if (!taskModalInvoice) return;
    if (!taskForm.invoicePdfUrl?.trim()) {
      setTaskFeedback({
        type: 'error',
        message: 'Debes adjuntar el enlace de la factura (PDF) antes de enviar las instrucciones.',
      });
      return;
    }
    setTaskModalSubmitting(true);
    try {
      const callable = httpsCallable(functions, 'createOperationalTask');

      let pdfUrl = taskForm.invoicePdfUrl.trim();
      const pdfStoragePath =
        taskModalInvoice.invoicePdfStoragePath || taskModalInvoice.metadata?.invoicePdfStoragePath || '';
      let resolvedClientId = taskModalInvoice.clientId || currentClientId || '';
      let resolvedClientEmail = (taskModalInvoice.clientEmail || currentClientEmail || '').toLowerCase();

      const invoiceDocClientId = resolvedClientId || currentClientId || null;
      let invoice = taskModalInvoice;
      if (invoiceDocClientId) {
        try {
          const invoiceRef = doc(db, 'clients', invoiceDocClientId, 'invoices', taskModalInvoice.id);
          const invoiceSnap = await getDoc(invoiceRef);
          if (invoiceSnap.exists()) {
            invoice = {
              id: invoiceSnap.id,
              clientId: invoiceDocClientId,
              ...invoiceSnap.data(),
              invoicePdfUrl: taskModalInvoice.invoicePdfUrl || invoiceSnap.data().invoicePdfUrl,
            };
            pdfUrl = invoice.invoicePdfUrl || pdfUrl;
          }
        } catch (mergeError) {
          console.warn('⚠️ No se pudo refrescar la factura antes de enviar instrucciones:', mergeError);
        }
      }

      const items = invoice.items || [];
      const totalItems = items.reduce((sum, item) => {
        const qty = parseFloat(item.quantity || 0);
        const price = parseFloat(item.price || 0);
        return sum + qty * price;
      }, 0);
      const total = invoice.total !== undefined ? parseFloat(invoice.total || 0) : totalItems;
      const deposit = parseFloat(invoice.deposit || 0);
      const discount = parseFloat(invoice.discount || 0);
      const payments = invoice.payments || [];
      const remaining = calculateRemaining({ total, deposit, discount, payments });
      resolvedClientId = invoice.clientId || resolvedClientId || currentClientId || '';
      resolvedClientEmail = (invoice.clientEmail || resolvedClientEmail || '').toLowerCase();
      const clientNameResolved = invoice.clientName || clientName || '';
      const clientPhoneResolved = invoice.clientPhone || clientPhoneField || '';
      const clientLocationResolved = invoice.projectLocation || clientLocationField || '';

      const metadataItems =
        (invoice.catalogSummary?.models && invoice.catalogSummary.models.length
          ? invoice.catalogSummary.models.map((model) => ({
              name: model.name || 'Modelo',
              quantity: Number(model.quantity || 1),
              notes: [
                model.height ? `${model.height}"` : null,
                model.width ? `${model.width}"` : null,
                model.series ? `Serie ${model.series}` : null,
              ]
                .filter(Boolean)
                .join(' · '),
            }))
          : items.map((item) => ({
              name: item.name || 'Modelo',
              quantity: parseFloat(item.quantity || 1),
              notes: item.description || item.notes || '',
            }))) || [];
      const metadataAccessories =
        (invoice.catalogSummary?.accessories && invoice.catalogSummary.accessories.length
          ? invoice.catalogSummary.accessories.map((addon) => ({
              name: addon.name || 'Accesorio',
              quantity: Number(addon.quantity || 1),
              notes: addon.description || '',
            }))
          : (invoice.accessories || []).map((addon) =>
              typeof addon === 'string'
                ? { name: addon }
                : {
                    name: addon.name || 'Accesorio',
                    quantity: addon.quantity,
                    notes: addon.notes || addon.description || '',
                  }
            )) || [];

      const clientIdentifiers = {};
      if (resolvedClientId) {
        clientIdentifiers.clientId = resolvedClientId;
        clientIdentifiers.clientDocId = resolvedClientId;
      }

      const payload = {
        type: taskForm.type,
        title: taskForm.title || `Orden ${invoice.id?.slice?.(-5) || ''}`,
        description:
          taskForm.notes ||
          `Instrucciones generadas desde la factura ${invoice.id?.slice?.(-5) || invoice.id || ''}.`,
        relatedOrderId: invoice.orderNumber || invoice.reference || invoice.id,
        relatedInvoiceId: invoice.id,
        relatedLeadId: invoice.relatedLeadId || invoice.leadId || '',
        ...clientIdentifiers,
        client: {
          name: clientNameResolved,
          email: resolvedClientEmail,
          phone: clientPhoneResolved,
          projectLocation: clientLocationResolved,
        },
        metadata: {
          lama: invoice.lama || invoice.metadata?.lama || '',
          glass: invoice.glass || invoice.metadata?.glass || '',
          color: invoice.color || invoice.metadata?.color || '',
          deposit,
          total,
          remaining,
          notes: taskForm.notes,
          invoiceUrl: pdfUrl,
          invoicePdfStoragePath: pdfStoragePath || undefined,
          clientEmail: resolvedClientEmail,
          ...(resolvedClientId ? { clientDocId: resolvedClientId } : {}),
          invoiceId: invoice.id,
          items: metadataItems,
          accessories: metadataAccessories,
        },
        attachments: [
          {
            type: 'invoice',
            invoiceId: invoice.id,
            url: pdfUrl,
            storagePath: pdfStoragePath || undefined,
            label: `Factura ${invoice.id?.slice?.(-5) || ''}`,
          },
        ],
        assignees: taskForm.assigneeEmail
          ? [
              {
                name: taskForm.assigneeName || taskForm.assigneeEmail,
                email: taskForm.assigneeEmail.trim(),
                role: taskForm.type,
              },
            ]
          : undefined,
        dueDate: taskForm.dueDate || null,
      };

      console.groupCollapsed('[AdminInvoices] Enviando instrucciones');
      console.log('Factura ID:', invoice.id);
      console.log('Cliente destino:', resolvedClientEmail, resolvedClientId);
      console.log('Payload resumido:', {
        type: payload.type,
        relatedInvoiceId: payload.relatedInvoiceId,
        relatedOrderId: payload.relatedOrderId,
        clientId: resolvedClientId || '(sin clientId)',
        clientEmail: resolvedClientEmail,
        items: payload.metadata.items?.length || 0,
        accessories: payload.metadata.accessories?.length || 0,
      });
      console.groupEnd();

      await callable(payload);
      console.info('[AdminInvoices] Instrucciones enviadas a createOperationalTask', {
        invoiceId: invoice.id,
        taskType: payload.type,
      });
      setTaskFeedback({ type: 'success', message: 'Instrucciones enviadas al equipo operativo.' });
      closeTaskModal();
    } catch (error) {
      console.error('❌ Error enviando instrucciones:', error);
      setTaskFeedback({
        type: 'error',
        message: error.message || 'No se pudo generar la tarea. Verifica la información obligatoria.',
      });
    } finally {
      setTaskModalSubmitting(false);
    }
  };

  const regenerateInvoicePdfLink = async () => {
    if (!taskModalInvoice) return;
    setRegeneratingPdf(true);
    setTaskFeedback(null);
    try {
      const url = await ensureInvoicePdfUrl(
        taskModalInvoice,
        clientName || taskModalInvoice.clientName || '',
        taskModalInvoice.clientEmail || currentClientEmail || ''
      );
      setTaskForm((prev) => ({ ...prev, invoicePdfUrl: url }));
      setTaskModalInvoice((prev) => ({ ...prev, invoicePdfUrl: url }));
    } catch (error) {
      console.error('❌ Error regenerando PDF:', error);
      setTaskFeedback({
        type: 'error',
        message: error.message || 'No se pudo regenerar el PDF. Intenta nuevamente.',
      });
    } finally {
      setRegeneratingPdf(false);
    }
  };


// Estados y lógica necesarios
const getStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "pagado":
      return "bg-green-100 text-green-800";
    case "pendiente":
      return "bg-yellow-100 text-yellow-800";
    case "parcial":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const calculateRemaining = ({ total = 0, deposit = 0, discount = 0, payments = [] }) => {
  const paymentsTotal = payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);
  return parseFloat(total) - parseFloat(deposit) - parseFloat(discount) - paymentsTotal;
};

return (
  <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
    <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 018 0v2m4 0v-2a8 8 0 00-16 0v2m16 0H5m16 0v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-1" />
            </svg>
            Facturas del Cliente
          </h2>

          <button
            onClick={handleNew}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg shadow hover:bg-orange-600 transition text-sm"
          >
            <HiPlus className="w-5 h-5" />
            Nueva Factura
          </button>
        </div>

        {taskFeedback && (
          <div
            className={`mb-6 rounded-2xl border px-4 py-3 text-sm ${
              taskFeedback.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
            }`}
          >
            {taskFeedback.message}
          </div>
        )}

        {/* Tabla Desktop */}
        <div className="overflow-x-auto hidden md:block bg-white shadow rounded-lg">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Depósito</th>
                <th className="px-4 py-3">Descuento</th>
                <th className="px-4 py-3">Pagos</th>
                <th className="px-4 py-3">Saldo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {invoices.map((inv) => {
                const totalItems = (inv.items || []).reduce((sum, item) => {
                  const qty = parseFloat(item.quantity || 0);
                  const price = parseFloat(item.price || 0);
                  return sum + qty * price;
                }, 0);

                const total = inv.total !== undefined ? parseFloat(inv.total) : totalItems;
                const deposit = parseFloat(inv.deposit || 0);
                const discount = parseFloat(inv.discount || 0);
                const payments = inv.payments || [];
                const remaining = calculateRemaining({ total, deposit, discount, payments });

                return (
                  <tr
                    key={inv.id}
                    className="hover:bg-gray-50 border-b cursor-pointer"
                    onClick={() => openInvoiceModal(inv)}
                  >
                    <td className="px-4 py-3">{inv.createdAt?.toDate?.().toLocaleDateString() || "—"}</td>
                    <td className="px-4 py-3 font-mono">{inv.id.slice(0, 6)}…</td>
                    <td className="px-4 py-3">${total.toFixed(2)}</td>
                    <td className="px-4 py-3">${deposit.toFixed(2)}</td>
                    <td className="px-4 py-3">${discount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      ${payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">${remaining.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(inv.status)}`}>
                        {inv.status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            await openTaskModalFromInvoice(inv);
                          }}
                          disabled={prefillingTaskId === inv.id}
                          className={`inline-flex items-center gap-1 text-sm ${
                            prefillingTaskId === inv.id
                              ? 'text-emerald-400 cursor-wait'
                              : 'text-emerald-600 hover:underline'
                          }`}
                        >
                          <HiPaperAirplane className="w-4 h-4" />
                          {prefillingTaskId === inv.id ? 'Preparando…' : 'Enviar instrucciones'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openInvoiceModal(inv);
                          }}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <HiPencil className="w-4 h-4" />
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile view */}
        <div className="md:hidden space-y-4">
          {invoices.map((inv) => {
            const totalItems = (inv.items || []).reduce((sum, item) => {
              const qty = parseFloat(item.quantity || 0);
              const price = parseFloat(item.price || 0);
              return sum + qty * price;
            }, 0);

            const total = inv.total !== undefined ? parseFloat(inv.total) : totalItems;
            const deposit = parseFloat(inv.deposit || 0);
            const discount = parseFloat(inv.discount || 0);
            const payments = inv.payments || [];
            const remaining = calculateRemaining({ total, deposit, discount, payments });

            return (
              <div
                key={inv.id}
                className="bg-white shadow rounded-lg p-4 cursor-pointer"
                onClick={() => openInvoiceModal(inv)}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-500">
                    {inv.createdAt?.toDate?.().toLocaleDateString() || "—"}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${getStatusColor(inv.status)}`}>
                    {inv.status || "—"}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  <p><strong>ID:</strong> <span className="font-mono">{inv.id.slice(0, 6)}…</span></p>
                  <p><strong>Total:</strong> ${total.toFixed(2)}</p>
                  <p><strong>Depósito:</strong> ${deposit.toFixed(2)}</p>
                  <p><strong>Descuento:</strong> ${discount.toFixed(2)}</p>
                  <p><strong>Pagos:</strong> ${payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0).toFixed(2)}</p>
                  <p><strong>Saldo:</strong> ${remaining.toFixed(2)}</p>
                </div>
                <div className="mt-3 grid gap-2">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      await openTaskModalFromInvoice(inv);
                    }}
                    disabled={prefillingTaskId === inv.id}
                    className={`w-full text-center text-sm flex items-center justify-center gap-1 ${
                      prefillingTaskId === inv.id
                        ? 'text-emerald-400 cursor-wait'
                        : 'text-emerald-600 hover:underline'
                    }`}
                  >
                    <HiPaperAirplane className="w-4 h-4" />
                    {prefillingTaskId === inv.id ? 'Preparando…' : 'Instrucciones'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openInvoiceModal(inv);
                    }}
                    className="w-full text-center text-sm text-blue-600 hover:underline flex items-center justify-center gap-1"
                  >
                    <HiPencil className="w-4 h-4" />
                    Editar
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal */}
        {showModal && (
          <InvoiceModal
            invoice={selectedInvoice}
            creating={creating}
            onClose={() => setShowModal(false)}
            onChange={setSelectedInvoice}
            onSave={saveInvoice}
            onAddPayment={addPayment}
            newPayment={newPayment}
            setNewPayment={setNewPayment}
            sendInvoiceEmail={sendInvoiceEmail}
            catalogProducts={catalogProducts}
            catalogAccessories={catalogAccessories}
            catalogLoading={catalogLoading}
          />
        )}

        {taskModalInvoice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8">
            <div className="max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
              <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gray-500">
                    Instrucciones operativas
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    Factura #{taskModalInvoice.id?.slice?.(-6) || taskModalInvoice.id}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {clientName || taskModalInvoice.clientName || 'Cliente sin nombre'} ·{' '}
                    {taskModalInvoice.createdAt?.toDate?.().toLocaleDateString?.('es-PR') || 'Sin fecha'}
                  </p>
                </div>
                <button
                  onClick={closeTaskModal}
                  className="rounded-full border border-gray-200 p-2 text-gray-500 hover:text-red-500"
                >
                  <HiXMark className="h-5 w-5" />
                </button>
              </div>

              <div className="grid gap-6 px-6 py-6 lg:grid-cols-2">
                <section className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Resumen de factura
                  </h4>
                  <dl className="mt-4 space-y-2 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <dt>Total</dt>
                      <dd className="font-semibold text-gray-900">
                        ${parseFloat(taskModalInvoice.total || 0).toFixed(2)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt>Depósito</dt>
                      <dd>${parseFloat(taskModalInvoice.deposit || 0).toFixed(2)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt>Saldo</dt>
                      <dd className="text-red-600 font-semibold">
                        $
                        {calculateRemaining({
                          total: parseFloat(taskModalInvoice.total || 0),
                          deposit: parseFloat(taskModalInvoice.deposit || 0),
                          discount: parseFloat(taskModalInvoice.discount || 0),
                          payments: taskModalInvoice.payments || [],
                        }).toFixed(2)}
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Modelos incluidos
                    </p>
                    <ul className="mt-2 max-h-40 space-y-2 overflow-auto pr-2 text-sm">
                      {(taskModalInvoice.items || []).map((item, idx) => (
                        <li key={`invoice-item-${idx}`} className="rounded-xl bg-white px-3 py-2 shadow-sm">
                          <p className="font-semibold text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">
                            {item.quantity || 1} uds · {item.description || 'Sin notas'}
                          </p>
                        </li>
                      ))}
                      {!taskModalInvoice.items?.length && (
                        <li className="text-xs text-gray-500">No se especificaron modelos en la factura.</li>
                      )}
                    </ul>
                  </div>
                  <p className="mt-4 text-xs text-gray-500">
                    Los equipos recibirán esta información junto con el enlace al PDF para garantizar que trabajen con
                    los mismos datos aprobados por el cliente.
                  </p>
                </section>

                <form onSubmit={submitTaskFromInvoice} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Título</label>
                    <input
                      name="title"
                      value={taskForm.title}
                      onChange={handleTaskFormChange}
                      className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      placeholder="Instalación residencial · Zona Metro"
                      required
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Tipo</label>
                      <select
                        name="type"
                        value={taskForm.type}
                        onChange={handleTaskFormChange}
                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      >
                        <option value="warehouse">Almacén / Producción</option>
                        <option value="technician">Instalación</option>
                        <option value="delivery">Entrega</option>
                        <option value="service">Servicio</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Fecha objetivo</label>
                      <input
                        type="date"
                        name="dueDate"
                        value={taskForm.dueDate}
                        onChange={handleTaskFormChange}
                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 flex items-center gap-2">
                      Enlace a la factura
                      <span className="text-[10px] font-bold uppercase text-emerald-600">Generado automáticamente</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        name="invoicePdfUrl"
                        value={taskForm.invoicePdfUrl}
                        readOnly
                        className="flex-1 rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 bg-gray-50"
                        placeholder="Generando PDF..."
                      />
                      <button
                        type="button"
                        onClick={regenerateInvoicePdfLink}
                        disabled={regeneratingPdf}
                        className="rounded-2xl border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {regeneratingPdf ? 'Creando…' : 'Regenerar'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Guardamos el PDF en Storage y lo compartimos con los equipos asignados. Puedes regenerarlo si actualizaste la factura.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Notas internas</label>
                    <textarea
                      name="notes"
                      value={taskForm.notes}
                      onChange={handleTaskFormChange}
                      rows={3}
                      className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      placeholder="Incluir medidas exactas, certificaciones solicitadas o recordatorios de cobro."
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                      Asignar responsable (opcional)
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        name="assigneeName"
                        value={taskForm.assigneeName}
                        onChange={handleTaskFormChange}
                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                        placeholder="Nombre"
                      />
                      <input
                        name="assigneeEmail"
                        value={taskForm.assigneeEmail}
                        onChange={handleTaskFormChange}
                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                        placeholder="correo@puertoricowindows.com"
                        type="email"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeTaskModal}
                      className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={taskModalSubmitting}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-200 hover:bg-emerald-700 disabled:opacity-60"
                    >
                      <HiPaperAirplane className="h-4 w-4" />
                      {taskModalSubmitting ? 'Enviando...' : 'Enviar instrucciones'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Botón enviar email */}
        {selectedInvoice && !showModal && !creating && (
          <div className="mt-6">
            <button
              className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition text-sm shadow"
              onClick={() => sendInvoiceEmail(selectedInvoice)}
            >
              <HiEnvelope className="w-5 h-5 text-white" />
              Enviar Factura por Email (PDF)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoices;
